<template>
  <transition name="fade">
    <div
      v-if="isOpen"
      class="fixed bottom-6 right-6 z-40 max-w-sm w-[calc(100%-3rem)]"
    >
      <UCard
        class="border border-neutral-200/80 bg-white/95 text-neutral-800 backdrop-blur shadow-xl"
        :ui="{
          base: 'rounded-2xl',
          divide: 'divide-y divide-neutral-200/70'
        }"
      >
        <template #header>
          
              
              <h2 class="text-lg font-semibold text-neutral-800">Privacy settings</h2>
          
            
          
        </template>

        <div class="space-y-2">
          <p class="text-xs text-neutral-700 mb-6">
            We use essential cookies to keep Kudo Sales running. No marketing cookies are required to browse.
          </p>

          <div class="space-y-2">
            <UCheckbox v-model="preferences.necessary" name="essential" label="Essential cookies" disabled />
            <UCheckbox v-model="preferences.marketing" name="marketing" label="Marketing cookies" />
          </div>

          <p class="text-xs text-neutral-700 mb-0">
            Learn more in our
            <ULink to="/privacy" class="underline-offset-4 hover:underline">
              privacy & cookie notice
            </ULink>.
          </p>
        </div>

        <template #footer>
          <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <UButton
              color="neutral"
              variant="outline"
              size="sm"
              class="sm:order-1 border-neutral-900 text-neutral-900 hover:bg-neutral-900/10"
              @click="rejectOptional"
            >
              Reject optional
            </UButton>
            <UButton
              color="neutral"
              variant="outline"
              size="sm"
              class="sm:order-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900/10"
              @click="savePreferences"
            >
              Save preferences
            </UButton>
            <UButton
              color="primary"
              size="sm"
              class="sm:order-3"
              @click="acceptAll"
            >
              Accept all
            </UButton>
          </div>
        </template>
      </UCard>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue'

type ConsentPreferences = {
  necessary: boolean
  marketing: boolean
  updatedAt: string
}

const consentCookie = useCookie<ConsentPreferences | null>('kudo_cookie_consent', {
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
  sameSite: 'lax'
})

const isOpen = ref(false)

const preferences = reactive({
  necessary: true,
  marketing: false
})

const applyPreferences = (value: { marketing: boolean }) => {
  preferences.marketing = value.marketing
}

const readConsent = () => {
  if (!consentCookie.value) {
    isOpen.value = true
    return
  }

  applyPreferences(consentCookie.value)
  if (consentCookie.value.marketing) {
    loadWisepops()
  }
  isOpen.value = false
}

const persistConsent = (value: { marketing: boolean }) => {
  applyPreferences(value)
  consentCookie.value = {
    necessary: true,
    marketing: preferences.marketing,
    updatedAt: new Date().toISOString()
  }
  if (preferences.marketing) {
    loadWisepops()
  }
  isOpen.value = false
}

const acceptAll = () => {
  persistConsent({ marketing: true })
}

const rejectOptional = () => {
  persistConsent({ marketing: false })
}

const savePreferences = () => {
  persistConsent({ marketing: preferences.marketing })
}

onMounted(() => {
  readConsent()
})

/**
 * Load Wisepops when marketing consent is granted.
 * Wisepops displays marketing/pop-up experiences and must not run without consent.
 */
const loadWisepops = () => {
  if (typeof window === 'undefined') {
    return
  }
  const global = window as typeof window & { wisepops?: (...args: unknown[]) => void }

  if (global.wisepops) {
    return
  }

  ;(function (w, i) {
    const target = window as typeof window & {
      [key: string]: ((...args: unknown[]) => void) & { q?: unknown[]; l?: number }
    }
    target[w] =
      target[w] ||
      function () {
        ;(target[w].q = target[w].q || []).push(arguments)
      }
    target[w].l = Date.now()
    const scriptEl = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]
    scriptEl.defer = true
    scriptEl.src = i
    firstScript?.parentNode?.insertBefore(scriptEl, firstScript)
  })('wisepops', 'https://wisepops.net/loader.js?v=3&h=hdB9dowssp')
}
</script>
